import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Col,
  Row,
  Card,
  CardBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  Alert,
} from "reactstrap"
import moment from "moment"
import * as XLSX from "xlsx"
import ImageUploadModal from "./ImageUploadModal"
import { parseSpecificUserRows } from "helpers/line_campaign_helper"

const Step1SelectCategory = ({
  validation,
  pageView,
  categoryOptions,
  surveyOptions,
  lineOaOptions = [],
}) => {
  const [showImageModal, setShowImageModal] = useState(false)
  const [imageModalTarget, setImageModalTarget] = useState(null)
  const fileInputRef = React.useRef(null)
  const [uploadError, setUploadError] = useState("")

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploadError("")

    const ext = file.name.split(".").pop().toLowerCase()
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setUploadError("Please upload an .xlsx, .xls or .csv file.")
      e.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result)
        const workbook = XLSX.read(data, { type: "array" })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        const lineUids = parseSpecificUserRows(rows)

        validation.setValues(values => ({ ...values, specificUserLineUids: lineUids, specificUserFileName: file.name }))
      } catch (err) {
        setUploadError(err.message || "Unable to read the uploaded file.")
      }
    }
    reader.onerror = () => setUploadError("Unable to read the uploaded file.")
    reader.readAsArrayBuffer(file)
    e.target.value = ""
  }

  const handleRemoveFile = () => {
    setUploadError("")
    validation.setFieldValue("specificUserLineUids", [])
    validation.setFieldValue("specificUserFileName", "")
  }

  const handleImageSave = (imageData) => {
    if (typeof imageModalTarget === "number") {
      const blocks = [...(validation.values.contentBlocks || [])]
      if (blocks[imageModalTarget]) {
        blocks[imageModalTarget] = {
          ...blocks[imageModalTarget],
          imageUrl: imageData.url || "",
          imageTitle: imageData.titleAttribute || "",
          imageAlt: imageData.altAttribute || "",
        }
        validation.setFieldValue("contentBlocks", blocks)
      }
      setImageModalTarget(null)
      setShowImageModal(false)
    }
  }

  const contentBlocks = validation.values.contentBlocks || []
  const linkOnly = validation.values.broadcastTimeType === "none"
  const selectLinkOnly = () => {
    if (pageView !== "view") validation.setValues(values => ({ ...values, broadcastTimeType: "none", scheduleDate: "", scheduleTime: "" }))
  }
  const isImageType = type => type === "image" || type === "pictures"
  const updateContentBlock = (index, updates) => {
    const blocks = [...contentBlocks]
    if (blocks[index]) blocks[index] = { ...blocks[index], ...updates }
    validation.setFieldValue("contentBlocks", blocks)
  }
  const addContentBlock = () => {
    const blocks = [...contentBlocks, { id: `block-${Date.now()}`, type: "text", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" }]
    validation.setFieldValue("contentBlocks", blocks)
  }
  const removeContentBlock = (index) => {
    const blocks = contentBlocks.filter((_, i) => i !== index)
    validation.setFieldValue("contentBlocks", blocks)
  }

  const handleCopyUrl = (url, label) => {
    if (url && pageView !== "create") {
      navigator.clipboard.writeText(url)
      alert(`${label} copied to clipboard!`)
    }
  }

  return (
    <>
      {/* Block: LINE OA Selection */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              LINE OA Selection
            </div>
            <CardBody>
              <Form>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="lineOaId" className="form-label">
                      LINE OA <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="lineOaId"
                      name="lineOaId"
                      type="select"
                      className="form-control"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.lineOaId || ""}
                      invalid={
                        validation.touched.lineOaId && validation.errors.lineOaId
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    >
                      <option value="">Select LINE OA</option>
                      {lineOaOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.oa_name}
                        </option>
                      ))}
                    </Input>
                    {validation.touched.lineOaId && validation.errors.lineOaId ? (
                      <FormFeedback type="invalid">
                        {validation.errors.lineOaId}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block: Survey Selection (Category + Survey Name + Survey URL) */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Survey Selection
            </div>
            <CardBody>
              <Form>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="categoryId" className="form-label">
                      Category <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="categoryId"
                      name="categoryId"
                      type="select"
                      className="form-control"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.categoryId || ""}
                      invalid={
                        validation.touched.categoryId && validation.errors.categoryId
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.category_name}
                        </option>
                      ))}
                    </Input>
                    {validation.touched.categoryId && validation.errors.categoryId ? (
                      <FormFeedback type="invalid">
                        {validation.errors.categoryId}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="surveyId" className="form-label">
                      Survey Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="surveyId"
                      name="surveyId"
                      type="select"
                      className="form-control"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.surveyId || ""}
                      invalid={
                        validation.touched.surveyId && validation.errors.surveyId
                          ? true
                          : false
                      }
                      disabled={pageView === "view" || !validation.values.categoryId}
                    >
                      <option value="">Select survey name</option>
                      {surveyOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Input>
                    {validation.touched.surveyId && validation.errors.surveyId ? (
                      <FormFeedback type="invalid">
                        {validation.errors.surveyId}
                      </FormFeedback>
                    ) : null}
                    {!validation.values.categoryId && (
                      <small className="text-muted d-block mt-1">
                        Please select a category first
                      </small>
                    )}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Label htmlFor="surveyUrl" className="form-label">
                      Survey Line URL
                    </Label>
                    {pageView === "create" && <p className="text-muted small">Save the campaign first to get its final link.</p>}
                    <div className="d-flex gap-2">
                      <Input
                        id="surveyUrl"
                        name="surveyUrl"
                        type="text"
                        className="form-control"
                        placeholder="www.xxxx.com"
                        value={validation.values.surveyUrl || ""}
                        disabled={true}
                      />
                      <Button
                        type="button"
                        color="secondary"
                        outline
                        onClick={() => handleCopyUrl(validation.values.surveyUrl, "Survey Line URL")}
                        disabled={pageView === "create" || !validation.values.surveyUrl}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        <i className="bx bx-copy me-1"></i>
                        Copy
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Label htmlFor="surveyMobileUrl" className="form-label">
                      Survey Mobile URL
                    </Label>
                    <div className="d-flex gap-2">
                      <Input
                        id="surveyMobileUrl"
                        name="surveyMobileUrl"
                        type="text"
                        className="form-control"
                        placeholder="www.xxxx.com"
                        value={validation.values.surveyMobileUrl || ""}
                        disabled={true}
                      />
                      <Button
                        type="button"
                        color="secondary"
                        outline
                        onClick={() => handleCopyUrl(validation.values.surveyMobileUrl, "Survey Mobile URL")}
                        disabled={pageView === "create" || !validation.values.surveyMobileUrl}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        <i className="bx bx-copy me-1"></i>
                        Copy
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block: Broadcast Settings */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Campaign Delivery
            </div>
            <CardBody>
              <Form>
                {/* Choose delivery before configuring a LINE broadcast. */}
                <Row className="mb-4 align-items-start">
                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap mb-2 mb-md-0" style={{ minWidth: "140px" }}>
                    <span className="form-label mb-0">Campaign delivery:</span>
                  </Col>
                  <Col>
                    <div className="d-flex flex-column gap-2">
                      <div
                        role="button"
                        tabIndex={0}
                        className="form-label mb-0 d-flex align-items-center"
                        style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                        onClick={() => pageView !== "view" && validation.setFieldValue("broadcastTimeType", "now")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && validation.setFieldValue("broadcastTimeType", "now") } }}
                      >
                        <Input
                          type="radio"
                          id="broadcastTimeNow"
                          name="broadcastTimeType"
                          value="now"
                          checked={validation.values.broadcastTimeType === "now"}
                          readOnly
                          style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                        />
                        Send now
                      </div>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <div
                          role="button"
                          tabIndex={0}
                          className="form-label mb-0 d-flex align-items-center"
                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                          onClick={() => pageView !== "view" && validation.setFieldValue("broadcastTimeType", "schedule")}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && validation.setFieldValue("broadcastTimeType", "schedule") } }}
                        >
                          <Input
                            type="radio"
                            id="broadcastTimeSchedule"
                            name="broadcastTimeType"
                            value="schedule"
                            checked={validation.values.broadcastTimeType === "schedule"}
                            readOnly
                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                          />
                          Schedule
                        </div>
                        {validation.values.broadcastTimeType === "schedule" && (
                          <>
                            <Input
                              name="scheduleDate"
                              type="date"
                              className="form-control"
                              style={{ width: "auto", minWidth: "150px" }}
                              value={validation.values.scheduleDate || ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.scheduleDate && validation.errors.scheduleDate)}
                              disabled={pageView === "view"}
                            />
                            {validation.touched.scheduleDate && validation.errors.scheduleDate && <FormFeedback>{validation.errors.scheduleDate}</FormFeedback>}
                            <Input
                              name="scheduleTime"
                              type="time"
                              className="form-control"
                              style={{ width: "auto", minWidth: "100px" }}
                              value={validation.values.scheduleTime || ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.scheduleTime && validation.errors.scheduleTime)}
                              disabled={pageView === "view"}
                            />
                            {validation.touched.scheduleTime && validation.errors.scheduleTime && <FormFeedback>{validation.errors.scheduleTime}</FormFeedback>}
                            <span className="text-muted small">UTC +7:00</span>
                          </>
                        )}
                      </div>
                      <div
                        role="radio"
                        aria-checked={linkOnly}
                        aria-disabled={pageView === "view"}
                        tabIndex={pageView === "view" ? -1 : 0}
                        className="form-label mb-0 d-flex align-items-center"
                        style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                        onClick={selectLinkOnly}
                        onKeyDown={event => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            selectLinkOnly()
                          }
                        }}
                      >
                        <Input
                          type="radio"
                          id="broadcastTimeNone"
                          name="broadcastTimeType"
                          value="none"
                          checked={linkOnly}
                          readOnly
                          aria-hidden="true"
                          tabIndex={-1}
                          disabled={pageView === "view"}
                          style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                        />
                        Publish without sending
                      </div>
                    </div>
                  </Col>
                </Row>

                {!linkOnly && <>

                <Row className="align-items-start">
                  <Col xs="12" md="auto" className="mb-2 mb-md-0" style={{ minWidth: "100px" }}>
                    <div className="fw-bold" style={{ fontSize: "1rem" }}>
                      Recipients
                    </div>
                  </Col>
                  <Col>
                    <div className="d-flex flex-column gap-2">
                      <div
                        role="button"
                        tabIndex={0}
                        className="form-label mb-0 d-flex align-items-center"
                        style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                        onClick={() => {
                          if (pageView !== "view") {
                            validation.setFieldValue("recipients", "all")
                            validation.setFieldValue("specificUserLineUids", [])
                            validation.setFieldValue("specificUserFileName", "")
                            validation.setFieldValue("recipientsLabel", "")
                            validation.setFieldValue("audienceGender", [])
                            validation.setFieldValue("audienceAge", [])
                            validation.setFieldValue("audienceOs", [])
                            validation.setFieldValue("audienceFriendshipPeriod", [])
                            validation.setFieldValue("audienceCity", [])
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            if (pageView !== "view") {
                              validation.setFieldValue("recipients", "all")
                              validation.setFieldValue("specificUserLineUids", [])
                              validation.setFieldValue("specificUserFileName", "")
                              validation.setFieldValue("recipientsLabel", "")
                              validation.setFieldValue("audienceGender", [])
                              validation.setFieldValue("audienceAge", [])
                              validation.setFieldValue("audienceOs", [])
                              validation.setFieldValue("audienceFriendshipPeriod", [])
                              validation.setFieldValue("audienceCity", [])
                            }
                          }
                        }}
                      >
                        <Input
                          type="radio"
                          id="recipientsAll"
                          name="recipients"
                          value="all"
                          checked={validation.values.recipients === "all"}
                          readOnly
                          style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                        />
                        All friends
                      </div>
                      <div className="d-flex flex-column">
                        <div
                          role="button"
                          tabIndex={0}
                          className="form-label mb-0 d-flex align-items-center"
                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                          onClick={() => pageView !== "view" && validation.setFieldValue("recipients", "targeting")}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && validation.setFieldValue("recipients", "targeting") } }}
                        >
                          <Input
                            type="radio"
                            id="recipientsTargeting"
                            name="recipients"
                            value="targeting"
                            checked={validation.values.recipients === "targeting"}
                            readOnly
                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                          />
                          Targeting
                        </div>
                        {validation.values.recipients === "targeting" && (
                          <div className="mt-1 ms-4">
                            <p className="text-muted small mb-0" style={{ fontSize: "0.875rem" }}>
                              You can narrow down your friends into smaller groups based on demographics or past actions.
                            </p>
                            <p className="text-muted small mb-0" style={{ fontSize: "0.875rem" }}>
                              This can make your broadcasts more effective
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {validation.values.recipients !== "all" && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <Label htmlFor="recipientsLabel" className="form-label fw-bold mb-0">
                            Recipients label (optional)
                          </Label>
                          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                            {(validation.values.recipientsLabel || "").length}/100
                          </span>
                        </div>
                        <Input
                          id="recipientsLabel"
                          name="recipientsLabel"
                          type="text"
                          className="form-control"
                          style={{ borderRadius: "6px", border: "1px solid #ced4da" }}
                          placeholder=""
                          maxLength={100}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.recipientsLabel || ""}
                          invalid={
                            validation.touched.recipientsLabel && validation.errors.recipientsLabel
                              ? true
                              : false
                          }
                          disabled={pageView === "view"}
                        />
                        {validation.touched.recipientsLabel && validation.errors.recipientsLabel ? (
                          <FormFeedback type="invalid">
                            {validation.errors.recipientsLabel}
                          </FormFeedback>
                        ) : null}
                        <small className="text-muted d-block mt-1" style={{ fontSize: "0.8125rem" }}>
                          Filter names help you stay organized. Find them under &quot;Target&quot; in your broadcast list.
                        </small>
                      </div>
                    )}

                    {/* Specific User - only show when Targeting */}
                    {validation.values.recipients === "targeting" && (
                      <div className="mt-3">
                        {uploadError && <Alert color="danger">{uploadError}</Alert>}
                        <div className="fw-bold mb-2" style={{ fontSize: "1rem", color: "#495057" }}>
                          Specific User
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls,.csv"
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                          />
                          <Button
                            type="button"
                            outline
                            color="success"
                            style={{ borderRadius: "8px", whiteSpace: "nowrap" }}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={pageView === "view"}
                          >
                            <i className="bx bx-upload me-1"></i>
                            Browse File
                          </Button>
                        {validation.values.specificUserFileName && (
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              padding: "0.4rem 0.75rem",
                              backgroundColor: "#fff",
                            }}
                          >
                            <span style={{ fontSize: "0.9rem" }}>{validation.values.specificUserFileName}</span>
                            {pageView !== "view" && (
                              <button
                                type="button"
                                className="btn-close"
                                style={{ fontSize: "0.6rem" }}
                                onClick={handleRemoveFile}
                              />
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                    )}

                    {validation.values.recipients !== "all" && (
                      <>
                        <hr className="my-3" style={{ borderColor: "#dee2e6" }} />

                        <div className="fw-bold mb-2" style={{ fontSize: "1rem", color: "#495057" }}>
                          Set Audiences
                        </div>
                        <div
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "8px",
                            padding: "1rem 1.25rem",
                            backgroundColor: "#fff",
                          }}
                        >
                          {(() => {
                            const toggleAudience = (field, value) => {
                              const arr = validation.values[field] || []
                              const next = arr.includes(value)
                                ? arr.filter(v => v !== value)
                                : [...arr, value]
                              validation.setFieldValue(field, next)
                            }
                            const isChecked = (field, value) => (validation.values[field] || []).includes(value)
                            const ageCol1 = ["15 - 19", "25 - 29", "35 - 39", "45 - 49"]
                            const ageCol2 = ["20 - 24", "30 - 34", "40 - 44", "50+"]
                            const cityOptions = [
                              "Taipei City", "New Taipei City", "Taoyuan City", "Taichung City", "Tainan City", "Kaohsiung City",
                              "Keelung City", "Hsinchu City", "Chiayi City", "Hsinchu County", "Miaoli County", "Changhua County",
                              "Hualien County", "Yilan County", "Chiayi County", "Pingtung County", "Taitung County", "Nantou County", "Yunlin County", "Penghu County", "Kinmen County", "Lienchiang County"
                            ]
                            return (
                              <>
                                <Row className="mb-3 align-items-start">
                                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap" style={{ minWidth: "120px" }}>
                                    <span className="form-label mb-0 fw-semibold" style={{ color: "#212529" }}>Gender:</span>
                                  </Col>
                                  <Col>
                                    <div className="d-flex flex-column gap-1">
                                      {["Male", "Female"].map(opt => (
                                        <div
                                          key={opt}
                                          role="button"
                                          tabIndex={0}
                                          className="form-label mb-0 d-flex align-items-center"
                                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                          onClick={() => pageView !== "view" && toggleAudience("audienceGender", opt)}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceGender", opt) } }}
                                        >
                                          <Input
                                            type="checkbox"
                                            id={`audienceGender-${opt}`}
                                            checked={isChecked("audienceGender", opt)}
                                            readOnly
                                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                          />
                                          {opt}
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                </Row>
                                <hr className="my-3" />
                                <Row className="mb-3 align-items-start">
                                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap" style={{ minWidth: "120px" }}>
                                    <span className="form-label mb-0 fw-semibold" style={{ color: "#212529" }}>Age:</span>
                                  </Col>
                                  <Col>
                                    <div className="row">
                                      <div className="col-6">
                                        <div className="d-flex flex-column gap-1">
                                          {ageCol1.map(opt => (
                                            <div
                                              key={opt}
                                              role="button"
                                              tabIndex={0}
                                              className="form-label mb-0 d-flex align-items-center"
                                              style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                              onClick={() => pageView !== "view" && toggleAudience("audienceAge", opt)}
                                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceAge", opt) } }}
                                            >
                                              <Input
                                                type="checkbox"
                                                id={`audienceAge-${opt}`}
                                                checked={isChecked("audienceAge", opt)}
                                                readOnly
                                                style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                              />
                                              {opt}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="col-6">
                                        <div className="d-flex flex-column gap-1">
                                          {ageCol2.map(opt => (
                                            <div
                                              key={opt}
                                              role="button"
                                              tabIndex={0}
                                              className="form-label mb-0 d-flex align-items-center"
                                              style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                              onClick={() => pageView !== "view" && toggleAudience("audienceAge", opt)}
                                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceAge", opt) } }}
                                            >
                                              <Input
                                                type="checkbox"
                                                id={`audienceAge-${opt}`}
                                                checked={isChecked("audienceAge", opt)}
                                                readOnly
                                                style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                              />
                                              {opt}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                                <hr className="my-3" />
                                <Row className="mb-3 align-items-start">
                                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap" style={{ minWidth: "120px" }}>
                                    <span className="form-label mb-0 fw-semibold" style={{ color: "#212529" }}>Operating System:</span>
                                  </Col>
                                  <Col>
                                    <div className="d-flex flex-column gap-1">
                                      {["iOS", "Android"].map(opt => (
                                        <div
                                          key={opt}
                                          role="button"
                                          tabIndex={0}
                                          className="form-label mb-0 d-flex align-items-center"
                                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                          onClick={() => pageView !== "view" && toggleAudience("audienceOs", opt)}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceOs", opt) } }}
                                        >
                                          <Input
                                            type="checkbox"
                                            id={`audienceOs-${opt}`}
                                            checked={isChecked("audienceOs", opt)}
                                            readOnly
                                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                          />
                                          {opt}
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                </Row>
                                <hr className="my-3" />
                                <Row className="mb-3 align-items-start">
                                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap" style={{ minWidth: "120px" }}>
                                    <span className="form-label mb-0 fw-semibold" style={{ color: "#212529" }}>Friendship Period:</span>
                                  </Col>
                                  <Col>
                                    <div className="d-flex flex-column gap-1">
                                      {["> 7 Days", "> 30 Days", "> 90 Days", "> 180 Days"].map(opt => (
                                        <div
                                          key={opt}
                                          role="button"
                                          tabIndex={0}
                                          className="form-label mb-0 d-flex align-items-center"
                                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                          onClick={() => pageView !== "view" && toggleAudience("audienceFriendshipPeriod", opt)}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceFriendshipPeriod", opt) } }}
                                        >
                                          <Input
                                            type="checkbox"
                                            id={`audienceFriendshipPeriod-${opt}`}
                                            checked={isChecked("audienceFriendshipPeriod", opt)}
                                            readOnly
                                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                          />
                                          {opt}
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                </Row>
                                <hr className="my-3" />
                                <Row className="mb-3 align-items-start">
                                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap" style={{ minWidth: "120px" }}>
                                    <span className="form-label mb-0 fw-semibold" style={{ color: "#212529" }}>City:</span>
                                  </Col>
                                  <Col>
                                    <div className="d-flex flex-column gap-1">
                                      {cityOptions.map(opt => (
                                        <div
                                          key={opt}
                                          role="button"
                                          tabIndex={0}
                                          className="form-label mb-0 d-flex align-items-center"
                                          style={{ cursor: pageView === "view" ? "default" : "pointer" }}
                                          onClick={() => pageView !== "view" && toggleAudience("audienceCity", opt)}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && toggleAudience("audienceCity", opt) } }}
                                        >
                                          <Input
                                            type="checkbox"
                                            id={`audienceCity-${opt}`}
                                            checked={isChecked("audienceCity", opt)}
                                            readOnly
                                            style={{ marginRight: "8px", flexShrink: 0, pointerEvents: "none" }}
                                          />
                                          {opt}
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                </Row>
                              </>
                            )
                          })()}
                        </div>
                      </>
                    )}
                  </Col>
                </Row>

                {/* Broadcast Details (inside Broadcast Settings) */}
                <Row className="mt-4 pt-3 align-items-start" style={{ borderTop: "1px solid #dee2e6" }}>
                  <Col xs="12" md="auto" className="pe-md-3 text-nowrap mb-2 mb-md-0" style={{ minWidth: "140px" }}>
                    <span className="form-label mb-0">Broadcast Details:</span>
                  </Col>
                  <Col>
                    <div className="d-flex flex-column gap-3">
                      {contentBlocks.map((block, index) => (
                        <div
                          key={block.id}
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "8px",
                            overflow: "hidden",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                          }}
                        >
                          {/* แถบเทา: radio + ปุ่มลบ */}
                          <div
                            className="d-flex align-items-center justify-content-between"
                            style={{
                              backgroundColor: "#e9ecef",
                              padding: "0.625rem 1rem",
                              borderBottom: "1px solid #dee2e6",
                            }}
                          >
                            <div className="d-flex gap-4">
                              <div
                                role="button"
                                tabIndex={0}
                                className="form-label mb-0 d-flex align-items-center"
                                style={{ cursor: pageView === "view" ? "default" : "pointer", fontWeight: 500, color: "#212529" }}
                                onClick={() => pageView !== "view" && updateContentBlock(index, { type: "text" })}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && updateContentBlock(index, { type: "text" }) } }}
                              >
                                <Input
                                  type="radio"
                                  id={`blockTypeText-${block.id}`}
                                  name={`blockType-${block.id}`}
                                  checked={block.type === "text"}
                                  readOnly
                                  style={{
                                    marginRight: "8px",
                                    flexShrink: 0,
                                    pointerEvents: "none",
                                    width: "1rem",
                                    height: "1rem",
                                    accentColor: "#34c38f",
                                  }}
                                />
                                Text
                              </div>
                              <div
                                role="button"
                                tabIndex={0}
                                className="form-label mb-0 d-flex align-items-center"
                                style={{ cursor: pageView === "view" ? "default" : "pointer", fontWeight: 500, color: "#212529" }}
                                onClick={() => pageView !== "view" && updateContentBlock(index, { type: "image" })}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pageView !== "view" && updateContentBlock(index, { type: "image" }) } }}
                              >
                                <Input
                                  type="radio"
                                  id={`blockTypePictures-${block.id}`}
                                  name={`blockType-${block.id}`}
                                  checked={isImageType(block.type)}
                                  readOnly
                                  style={{
                                    marginRight: "8px",
                                    flexShrink: 0,
                                    pointerEvents: "none",
                                    width: "1rem",
                                    height: "1rem",
                                    accentColor: "#34c38f",
                                  }}
                                />
                                Pictures
                              </div>
                            </div>
                            {pageView !== "view" && contentBlocks.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-link p-0 text-danger"
                                style={{ fontSize: "1.25rem", lineHeight: 1 }}
                                onClick={() => removeContentBlock(index)}
                                title="Delete"
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            )}
                          </div>
                          {/* พื้นที่ content พื้นหลังขาว */}
                          <div
                            style={{
                              backgroundColor: "#fff",
                              padding: "1rem 1.25rem",
                            }}
                          >
                            {isImageType(block.type) ? (
                              <>
                                <div className="fw-bold mb-2" style={{ fontSize: "0.95rem", color: "#212529" }}>
                                  Picture
                                </div>
                                <p className="text-muted mb-2" style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                  File type png, jpg, jpeg only, Max filesize 10 MB
                                </p>
                                <div
                                  onClick={() => { if (pageView !== "view") { setImageModalTarget(index); setShowImageModal(true) } }}
                                  style={{
                                    width: "100%",
                                    maxWidth: "280px",
                                    height: "180px",
                                    backgroundColor: "#f8f9fa",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: pageView === "view" ? "not-allowed" : "pointer",
                                    overflow: "hidden",
                                  }}
                                >
                                  {block.imageUrl ? (
                                    <img src={block.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    <div style={{ textAlign: "center", color: "#adb5bd" }}>
                                      <i className="bx bx-image" style={{ fontSize: "48px", display: "block", marginBottom: "4px" }}></i>
                                      <span style={{ fontSize: "0.875rem" }}>Upload image</span>
                                    </div>
                                  )}
                                </div>
                                <Row className="mt-3">
                                  <Col md={6}>
                                    <Label className="form-label" style={{ fontSize: "0.875rem" }}>
                                      Title Attribute : {block.imageTitle || "-"}
                                    </Label>
                                  </Col>
                                  <Col md={6}>
                                    <Label className="form-label" style={{ fontSize: "0.875rem" }}>
                                      Alt Attribute : {block.imageAlt || "-"}
                                    </Label>
                                  </Col>
                                </Row>
                              </>
                            ) : (
                              <Input
                                type="textarea"
                                className="form-control"
                                placeholder="Enter Text"
                                rows={4}
                                value={block.textContent || ""}
                                onChange={(e) => updateContentBlock(index, { textContent: e.target.value })}
                                disabled={pageView === "view"}
                                style={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "6px",
                                  padding: "0.75rem 1rem",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      {pageView !== "view" && (
                        <Button
                          onClick={addContentBlock}
                          style={{
                            alignSelf: "flex-start",
                            backgroundColor: "#34c38f",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            padding: "0.5rem 1.25rem",
                            fontWeight: 500,
                          }}
                        >
                          <i className="bx bx-plus me-1"></i> Add
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
                </>}
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block: Schedule Time */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Campaign Information
            </div>
            <CardBody>
              <Form>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="campaignName" className="form-label">
                      Campaign Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="campaignName"
                      name="campaignName"
                      type="text"
                      className="form-control"
                      placeholder="Enter Campaign Name"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.campaignName || ""}
                      invalid={
                        validation.touched.campaignName && validation.errors.campaignName
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.campaignName && validation.errors.campaignName ? (
                      <FormFeedback type="invalid">
                        {validation.errors.campaignName}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="startDate" className="form-label">
                      Start Date: <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="startDate"
                      name="startDate"
                      type="date"
                      className="form-control"
                      min={moment().format("YYYY-MM-DD")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.startDate || ""}
                      invalid={
                        validation.touched.startDate && validation.errors.startDate
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.startDate && validation.errors.startDate ? (
                      <FormFeedback type="invalid">
                        {validation.errors.startDate}
                      </FormFeedback>
                    ) : null}
                  </Col>

                  <Col md={4}>
                    <Label htmlFor="endDate" className="form-label">
                      End Date: <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="endDate"
                      name="endDate"
                      type="date"
                      className="form-control"
                      min={validation.values.startDate || moment().format("YYYY-MM-DD")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.endDate || ""}
                      invalid={
                        validation.touched.endDate && validation.errors.endDate
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.endDate && validation.errors.endDate ? (
                      <FormFeedback type="invalid">
                        {validation.errors.endDate}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Image Upload Modal (for content block Pictures only) */}
      <ImageUploadModal
        isOpen={showImageModal}
        toggle={() => { setShowImageModal(false); setImageModalTarget(null) }}
        onSave={handleImageSave}
        currentImage={
          typeof imageModalTarget === "number" && contentBlocks[imageModalTarget]
            ? contentBlocks[imageModalTarget].imageUrl
            : ""
        }
        currentTitle={
          typeof imageModalTarget === "number" && contentBlocks[imageModalTarget]
            ? contentBlocks[imageModalTarget].imageTitle
            : ""
        }
        currentAlt={
          typeof imageModalTarget === "number" && contentBlocks[imageModalTarget]
            ? contentBlocks[imageModalTarget].imageAlt
            : ""
        }
      />
    </>
  )
}

Step1SelectCategory.propTypes = {
  validation: PropTypes.object.isRequired,
  pageView: PropTypes.string,
  categoryOptions: PropTypes.array.isRequired,
  surveyOptions: PropTypes.array.isRequired,
  lineOaOptions: PropTypes.array,
}

export default Step1SelectCategory
